import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Upload, Download, Trash2, FileText, AlertCircle, CheckCircle2, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AnimatedSection from '../components/AnimatedSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Product Manuals',
  'Software & Drivers',
  'Datasheets & Specifications',
  'Installation Guides',
  'Video Tutorials',
  'Catalog'
];

const BRANDS = [
  { name: 'All', color: 'ewss-cyan' },
  { name: 'Supmea', color: 'ewss-red' },
  { name: 'Haiwell', color: 'ewss-cyan' },
  { name: 'Savch', color: 'ewss-orange' },
  { name: 'Yotta Control', color: 'ewss-green' }
];

const Resources = () => {
  const { user, isAdmin } = useAuth();
  
  const [resources, setResources] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [downloadingIds, setDownloadingIds] = useState(new Set()); // Track which files are downloading
  
  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadCategory, setUploadCategory] = useState('Product Manuals');
  const [uploadBrand, setUploadBrand] = useState('Haiwell');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const { data } = await axios.get(`${API}/resources/list`, {
        withCredentials: true
      });
      setResources(data);
    } catch (error) {
      // If not authenticated, still try to fetch without credentials for public view
      if (error.response?.status === 401) {
        try {
          const { data } = await axios.get(`${API}/resources/list`);
          setResources(data);
        } catch (err) {
          console.error('Error fetching resources:', err);
        }
      } else {
        console.error('Error fetching resources:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const getFilteredResources = () => {
    let filtered = resources;

    if (searchTerm) {
      filtered = filtered.filter(r =>
        r.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.filename.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedBrand !== 'All') {
      filtered = filtered.filter(r => r.brand === selectedBrand);
    }

    return filtered;
  };

  const groupByBrand = (resourcesList) => {
    const grouped = {};
    resourcesList.forEach(resource => {
      if (!grouped[resource.brand]) {
        grouped[resource.brand] = [];
      }
      grouped[resource.brand].push(resource);
    });
    return grouped;
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploadError('');
    setUploadSuccess('');

    if (!uploadFile) {
      setUploadError('Please select a file');
      return;
    }

    const maxSize = 100 * 1024 * 1024;
    if (uploadFile.size > maxSize) {
      setUploadError('File size exceeds 100MB limit');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      await axios.post(
        `${API}/resources/upload?title=${encodeURIComponent(uploadTitle)}&description=${encodeURIComponent(uploadDescription)}&category=${encodeURIComponent(uploadCategory)}&brand=${encodeURIComponent(uploadBrand)}`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setUploadSuccess('File uploaded successfully!');
      setTimeout(() => {
        setShowUploadModal(false);
        resetUploadForm();
        fetchResources();
      }, 2000);
    } catch (error) {
      setUploadError(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadTitle('');
    setUploadDescription('');
    setUploadCategory('Product Manuals');
    setUploadBrand('Haiwell');
    setUploadError('');
    setUploadSuccess('');
  };

  const handleDelete = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) {
      return;
    }

    try {
      await axios.delete(`${API}/resources/${resourceId}`, {
        withCredentials: true
      });
      fetchResources();
    } catch (error) {
      alert('Error deleting resource: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDownload = async (resourceId, filename) => {
    // Check if user is logged in
    if (!user) {
      alert('Please login or register to download resources.');
      window.location.href = '/login';
      return;
    }

    // Add to downloading set
    setDownloadingIds(prev => new Set(prev).add(resourceId));

    try {
      const response = await axios.get(`${API}/resources/download/${resourceId}`, {
        withCredentials: true,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Show success message
      alert(`✅ ${filename} downloaded successfully!`);
      
    } catch (error) {
      if (error.response?.status === 401) {
        alert('Your session has expired. Please login again.');
        window.location.href = '/login';
      } else {
        alert('Error downloading file: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      // Remove from downloading set
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(resourceId);
        return newSet;
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 10) / 10 + ' ' + sizes[i];
  };

  const getBrandColor = (brandName) => {
    const brand = BRANDS.find(b => b.name === brandName);
    return brand ? brand.color : 'ewss-cyan';
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-gray-400">Loading resources...</div>
      </div>
    );
  }

  const filteredResources = getFilteredResources();
  const groupedResources = groupByBrand(filteredResources);
  const brandOrder = ['Supmea', 'Haiwell', 'Savch', 'Yotta Control'];

  return (
    <div className="min-h-screen pt-20 bg-ewss-navy">
      {/* Header Section */}
      <section className="py-12 bg-ewss-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start justify-between mb-8">
            <div>
              <p className="text-ewss-cyan text-sm font-semibold mb-2 uppercase tracking-wider">Resources</p>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Downloads
              </h1>
              <p className="text-gray-400 text-lg max-w-2xl">
                Browse and download product datasheets, manuals, software and technical documents.
              </p>
            </div>
            
            {isAdmin && (
              <button
                onClick={() => setShowUploadModal(true)}
                className="inline-flex items-center px-6 py-3 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:shadow-[0_0_30px_rgba(23,145,255,0.5)] transition-all duration-300"
              >
                <Upload className="mr-2" size={20} />
                Upload File
              </button>
            )}
          </div>

          {/* Brand Filter Buttons */}
          <div className="flex flex-wrap gap-3 mb-8">
            {BRANDS.map(brand => (
              <button
                key={brand.name}
                onClick={() => setSelectedBrand(brand.name)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  selectedBrand === brand.name
                    ? 'bg-ewss-cyan text-ewss-navy shadow-lg'
                    : 'bg-transparent text-gray-400 border-2 border-gray-700 hover:border-ewss-cyan hover:text-ewss-cyan'
                }`}
              >
                {brand.name}
              </button>
            ))}
          </div>

          {/* Search Bar */}
          <div className="mb-8">
            <div className="relative max-w-2xl">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search files..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-black/30 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-ewss-cyan focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Resources by Brand */}
      <section className="pb-16 bg-ewss-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredResources.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No resources found.</p>
            </div>
          ) : (
            <div className="space-y-12">
              {brandOrder.map(brandName => {
                const brandResources = groupedResources[brandName];
                if (!brandResources || brandResources.length === 0) return null;

                const brandColor = getBrandColor(brandName);

                return (
                  <div key={brandName}>
                    {/* Brand Badge */}
                    <div className="mb-6">
                      <span className={`inline-block px-4 py-2 bg-${brandColor} text-white font-semibold rounded-full text-sm`}>
                        {brandName}
                      </span>
                    </div>

                    {/* Files Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {brandResources.map((resource, index) => (
                        <AnimatedSection key={resource.id} delay={index * 0.05}>
                          <div className="bg-black/40 backdrop-blur-sm rounded-xl p-5 border border-gray-700/50 hover:border-ewss-cyan/50 transition-all duration-300 flex items-center gap-4">
                            {/* File Icon */}
                            <div className="flex-shrink-0 w-12 h-12 bg-ewss-cyan/10 rounded-lg flex items-center justify-center">
                              <FileText className="text-ewss-cyan" size={24} />
                            </div>

                            {/* File Info */}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-white font-bold text-base mb-1 truncate">
                                {resource.title}
                              </h3>
                              <p className="text-gray-400 text-sm mb-2 truncate">
                                {resource.description}
                              </p>
                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                <span className="font-medium">PDF</span>
                                <span>{formatFileSize(resource.file_size)}</span>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex-shrink-0 flex items-center gap-2">
                              <button
                                onClick={() => handleDownload(resource.id, resource.filename)}
                                disabled={downloadingIds.has(resource.id)}
                                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all ${
                                  downloadingIds.has(resource.id)
                                    ? 'bg-gray-600 cursor-not-allowed'
                                    : 'bg-ewss-cyan/10 hover:bg-ewss-cyan text-ewss-cyan hover:text-ewss-navy'
                                }`}
                                title={downloadingIds.has(resource.id) ? "Downloading..." : "Download"}
                              >
                                {downloadingIds.has(resource.id) ? (
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                ) : (
                                  <Download size={18} />
                                )}
                              </button>
                              {isAdmin && (
                                <button
                                  onClick={() => handleDelete(resource.id)}
                                  className="w-10 h-10 flex items-center justify-center bg-red-500/10 hover:bg-red-500 text-red-400 hover:text-white rounded-lg transition-all"
                                  title="Delete"
                                >
                                  <Trash2 size={18} />
                                </button>
                              )}
                            </div>
                          </div>
                        </AnimatedSection>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-ewss-navy border border-ewss-cyan/30 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Upload Resource</h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  resetUploadForm();
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {uploadSuccess && (
              <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg flex items-start gap-3">
                <CheckCircle2 className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-green-400 text-sm">{uploadSuccess}</p>
              </div>
            )}

            {uploadError && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
                <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-red-400 text-sm">{uploadError}</p>
              </div>
            )}

            <form onSubmit={handleUpload} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  File (Max 100MB)
                </label>
                <input
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="block w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-ewss-cyan file:text-ewss-navy file:font-semibold hover:file:bg-ewss-cyan/80"
                  required
                />
                {uploadFile && (
                  <p className="mt-2 text-sm text-gray-400">
                    Selected: {uploadFile.name} ({formatFileSize(uploadFile.size)})
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                <input
                  type="text"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  className="block w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-ewss-cyan focus:border-transparent"
                  placeholder="e.g., Haiwell PLC Programming Manual"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  rows="3"
                  className="block w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-ewss-cyan focus:border-transparent"
                  placeholder="Describe the resource..."
                  required
                ></textarea>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                  <select
                    value={uploadCategory}
                    onChange={(e) => setUploadCategory(e.target.value)}
                    className="block w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-ewss-cyan focus:border-transparent"
                    required
                  >
                    {CATEGORIES.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Brand</label>
                  <select
                    value={uploadBrand}
                    onChange={(e) => setUploadBrand(e.target.value)}
                    className="block w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-ewss-cyan focus:border-transparent"
                    required
                  >
                    {BRANDS.filter(b => b.name !== 'All').map(brand => (
                      <option key={brand.name} value={brand.name}>{brand.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    resetUploadForm();
                  }}
                  className="flex-1 py-3 border-2 border-gray-600 text-gray-300 font-semibold rounded-lg hover:bg-gray-600/20 transition-all duration-300"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:shadow-[0_0_30px_rgba(23,145,255,0.5)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Resources;
